<template>
  <svg :id="svgId"></svg>
</template>

<script>
import * as d3 from 'd3';

export default {
  props: {
    dataInput: Array,
    width: Number,
    height: Number,
  },
  data() {
    return {
      svgId: `piechart-${this._uid}`,
    };
  },
  mounted() {
    this.generateChart();
  },
  watch: {
    dataInput() {
      this.generateChart();
    },
  },
  methods: {
    generateChart() {
      const uniqueColors = [
        "#F69292", "#F8B534", "#FACA9B", "#FE0000", "#AAE066", "#B4A1CD",
        "#00FFF0", "#6DE7AC", "#0FA958", "#0F65A9", "#6092C0", "#F9FD41", "#9747FF"
      ];
      const uniquePatterns = ['solid', 'striped', 'dotted', 'grid'];

      let data = this.dataInput.map(d => ({ ...d })); // clone data to avoid mutation

      // If all values are zero, assign small default values to visualize
      const allZero = data.every(d => d.value === 0);
      if (allZero) {
        data.forEach(d => d.value = 1);
      }

      // Margins for neat layout and legend space
      const margin = { top: 30, right: 180, bottom: 30, left: 30 };
      const width = this.width;
      const height = this.height;
      const radius = Math.min(width, height) / 2;

      // Clear previous chart
      const container = d3.select(`#${this.svgId}`);
      container.selectAll('*').remove();

      // Append SVG group centered for pie chart
      const svg = container
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .style('font-family', 'Times New Roman')
        .append('g')
        .attr('transform', `translate(${width / 2 + margin.left}, ${height / 2 + margin.top})`);

      // Draw bounding box (around full chart area including legend)
      svg
        .append('rect')
        .attr('x', -width / 2 - margin.left)
        .attr('y', -height / 2 - margin.top)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .attr('rx', 12)
        .attr('fill', 'none')
        .attr('stroke', '#ccc')
        .attr('stroke-width', 1);

      // Helper to create pattern fills
      function createPattern(index, type) {
        const patternId = `pattern-${index}`;
        const defs = svg.select('defs').empty() ? svg.append('defs') : svg.select('defs');
        let pattern = defs.select(`#${patternId}`);

        if (!pattern.node()) {
          pattern = defs.append('pattern')
            .attr('id', patternId)
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('width', 10)
            .attr('height', 10);

          // Background rect with color
          pattern.append('rect')
            .attr('width', 10)
            .attr('height', 10)
            .attr('fill', uniqueColors[index % uniqueColors.length]);

          // Add pattern shapes
          if (type === 'dotted') {
            pattern.append('circle')
              .attr('cx', 5)
              .attr('cy', 5)
              .attr('r', 2)
              .attr('fill', 'white');
          } else if (type === 'striped') {
            pattern.append('line')
              .attr('x1', 0)
              .attr('y1', 10)
              .attr('x2', 10)
              .attr('y2', 0)
              .attr('stroke', 'white')
              .attr('stroke-width', 1);
          } else if (type === 'grid') {
            pattern.append('rect')
              .attr('x', 3)
              .attr('y', 3)
              .attr('width', 4)
              .attr('height', 4)
              .attr('fill', 'white');
          }
          // For 'solid' (or default) no additional shapes
        }
        return `url(#${patternId})`;
      }

      // Create pie layout
      const pie = d3.pie().value(d => d.value);
      const arcsData = pie(data);

      // Arc generator
      const arc = d3.arc()
        .innerRadius(0)
        .outerRadius(radius)
        .padAngle(0.005);

      // Draw arcs
      const arcs = svg.selectAll('.arc')
        .data(arcsData)
        .enter()
        .append('g')
        .attr('class', 'arc');

      arcs.append('path')
        .attr('d', arc)
        .attr('fill', (d, i) => {
          const patternType = uniquePatterns[i % uniquePatterns.length];
          return createPattern(i, patternType);
        })
        .attr('stroke', 'white')
        .attr('stroke-width', 1);

      // Legend group positioned to the right of the pie
      const legendX = radius + 30;
      const legendY = -radius;

      const legend = svg.append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(${legendX}, ${legendY})`);

      // Two columns in legend, adjust spacing accordingly
      const legendItemHeight = 20;
      const itemsPerColumn = 15; // change as needed for data size

      const legendItems = legend.selectAll('.legend-item')
        .data(arcsData)
        .enter()
        .append('g')
        .attr('class', 'legend-item')
        .attr('transform', (d, i) => {
          const col = Math.floor(i / itemsPerColumn);
          const row = i % itemsPerColumn;
          return `translate(${col * 160}, ${row * legendItemHeight})`;
        });

      // Colored boxes with patterns
      legendItems.append('rect')
        .attr('width', 14)
        .attr('height', 14)
        .attr('fill', (d, i) => {
          const patternType = uniquePatterns[i % uniquePatterns.length];
          return createPattern(i, patternType);
        })
        .attr('stroke', '#ccc')
        .attr('stroke-width', 1);

      // Legend text, vertically centered relative to box
      legendItems.append('text')
        .attr('x', 20)
        .attr('y', 12)
        .style('font-size', '13px')
        .style('fill', '#333')
        .text(d => `${d.data.variable} (${((d.endAngle - d.startAngle) / (2 * Math.PI) * 100).toFixed(1)}%)`);
    }
  },
};
</script>
